import { useCallback, useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { ActiveUser, useRealtimeCollaboration } from './useRealtimeCollaboration';

interface UseOrderViewersOptions {
  /**
   * The pronumber (or other identifier) of the order currently open in the
   * details drawer. Pass null when no order is open. Changes here drive the
   * broadcast lifecycle: a new id triggers a viewing_started broadcast and
   * cleanup fires viewing_stopped — so closing the drawer, switching to a
   * different order, and unmounting are all covered.
   */
  activeOrderId: number | string | null;
}

interface UseOrderViewersReturn {
  /** Map of orderId (stringified) -> the active viewers for that order. */
  orderViewers: Record<string, ActiveUser[]>;
}

/**
 * Tracks which users are currently viewing each order. All callers share a
 * single global "order-viewers" room so users on different host pages
 * (DispatchPlanner, RepairOrders, SearchResults, etc.) can still see each
 * other when looking at the same order.
 *
 * Consumers receive the raw viewers map; the rendering site is responsible
 * for filtering self / deduping by user_id when displaying the list.
 */
export function useOrderViewers({ activeOrderId }: UseOrderViewersOptions): UseOrderViewersReturn {
  const [orderViewers, setOrderViewers] = useState<Record<string, ActiveUser[]>>({});
  const { accounts } = useMsal();
  // Pull stable primitives out of currentUser so the effect below doesn't
  // re-fire on every render just because MSAL handed us a new object ref —
  // that would cause a stop/start broadcast flicker for other viewers.
  const userName = accounts[0]?.name || accounts[0]?.username || 'Anonymous';
  const userId = accounts[0]?.localAccountId || accounts[0]?.homeAccountId || 'unknown';
  const userEmail = accounts[0]?.username;

  const handleLiveUpdate = useCallback((update: { update_type: string; payload: any }) => {
    if (update.update_type === 'order_viewing_started') {
      const { orderId, viewer } = update.payload;
      setOrderViewers((prev) => ({
        ...prev,
        [orderId]: [...(prev[orderId] || []), viewer],
      }));
    } else if (update.update_type === 'order_viewing_stopped') {
      const { orderId, viewerId } = update.payload;
      setOrderViewers((prev) => ({
        ...prev,
        [orderId]: (prev[orderId] || []).filter((v) => v.sid !== viewerId),
      }));
    }
  }, []);

  const { broadcastUpdate, isConnected, socket } = useRealtimeCollaboration({
    pageId: 'order-viewers',
    userName,
    userId,
    userEmail,
    onLiveUpdate: handleLiveUpdate,
  });

  useEffect(() => {
    if (!isConnected || !socket?.id || !activeOrderId) return;

    const orderIdKey = activeOrderId.toString();
    const sid = socket.id;
    const viewer: ActiveUser = {
      sid,
      user_name: userName,
      user_id: userId,
      user_email: userEmail,
    };

    broadcastUpdate('order_viewing_started', { orderId: orderIdKey, viewer });
    setOrderViewers((prev) => ({
      ...prev,
      [orderIdKey]: [...(prev[orderIdKey] || []), viewer],
    }));

    return () => {
      broadcastUpdate('order_viewing_stopped', { orderId: orderIdKey, viewerId: sid });
      setOrderViewers((prev) => ({
        ...prev,
        [orderIdKey]: (prev[orderIdKey] || []).filter((v) => v.sid !== sid),
      }));
    };
  }, [activeOrderId, isConnected, socket?.id, userName, userId, userEmail, broadcastUpdate]);

  return { orderViewers };
}
